import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authService, RegisterData } from '../../services/auth';
import api from '../../services/api';
import { useTranslation } from 'react-i18next';

interface Building {
  id: number;
  name: string;
  address: string;
}

interface Floor {
  id: number;
  floor_number: number;
  floor_name: string;
  building_id: number;
}

interface Flat {
  id: number;
  flat_number: string;
  flat_type: string;
  bedrooms: number;
  bathrooms: number;
  area_sqft?: number;
  status: 'available' | 'occupied';
  floor_id: number;
}

const Register: React.FC = () => {
  const [formData, setFormData] = useState<RegisterData>({
    name: '',
    email: '',
    password: '',
    phone: '',
    flat_no: '',
    building_id: 0,
    floor_id: undefined,
    role: 'resident',
    user_type: 'resident'
  });
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [floors, setFloors] = useState<Floor[]>([]);
  const [flats, setFlats] = useState<Flat[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const fetchBuildings = async () => {
      try {
        const response = await api.get('/public/buildings');
        const data = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.data)
            ? response.data.data
            : [];
        setBuildings(data);
      } catch (error) {
        console.error('Error fetching buildings:', error);
      }
    };

    fetchBuildings();
  }, []);

  const fetchFloors = async (buildingId: number) => {
    try {
      const response = await api.get(`/buildings/${buildingId}/floors`);
      const data = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.data)
          ? response.data.data
          : [];
      setFloors(data);
      setFlats([]); // Reset flats when building changes
    } catch (error) {
      console.error('Error fetching floors:', error);
    }
  };

  const fetchFlats = async (floorId: number) => {
    try {
      const response = await api.get(`/floors/${floorId}/flats`);
      const data = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.data)
          ? response.data.data
          : [];
      setFlats(data as Flat[]);
    } catch (error) {
      console.error('Error fetching flats:', error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    if (name === 'building_id') {
      const buildingId = parseInt(value) || 0;
      setFormData({
        ...formData,
        building_id: buildingId,
        flat_no: '' // Reset flat selection
      });
      
      if (buildingId > 0) {
        fetchFloors(buildingId);
      } else {
        setFloors([]);
        setFlats([]);
      }
    } else if (name === 'floor_id') {
      const floorId = parseInt(value) || undefined;
      setFormData({
        ...formData,
        floor_id: floorId,
        flat_no: '' // Reset flat selection
      });
      
      if (floorId && floorId > 0) {
        fetchFlats(floorId);
      } else {
        setFlats([]);
      }
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await authService.register(formData);
      navigate('/resident');
    } catch (error: any) {
      setError(error.response?.data?.message || t('auth.register.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-form">
        <h2>{t('auth.register.title')}</h2>
        {error && <div className="error-message">{error}</div>}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">{t('auth.register.name')}</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="email">{t('auth.register.email')}</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">{t('auth.register.password')}</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              minLength={6}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="phone">{t('auth.register.phone')}</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="building_id">{t('auth.register.building')}</label>
            <select
              id="building_id"
              name="building_id"
              value={formData.building_id}
              onChange={handleChange}
              required
            >
              <option value={0}>{t('auth.register.selectBuilding')}</option>
              {buildings.map(building => (
                <option key={building.id} value={building.id}>
                  {building.name} - {building.address}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="floor_id">{t('auth.register.floor')}</label>
            <select
              id="floor_id"
              name="floor_id"
              value={formData.floor_id || ''}
              onChange={handleChange}
              required
              disabled={!formData.building_id}
            >
              <option value="">{t('auth.register.selectFloor')}</option>
              {floors.map(floor => (
                <option key={floor.id} value={floor.id}>
                  {t('auth.register.floorOption', { number: floor.floor_number, name: floor.floor_name })}
                </option>
              ))}
            </select>
            {formData.building_id && floors.length === 0 && (
              <small className="text-muted">{t('auth.register.loadingFloors')}</small>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="flat_no">{t('auth.register.flat')}</label>
            <select
              id="flat_no"
              name="flat_no"
              value={formData.flat_no}
              onChange={handleChange}
              required
              disabled={!formData.floor_id || formData.floor_id === 0}
            >
              <option value="">{t('auth.register.selectFlat')}</option>
              {flats.map(flat => (
                <option key={flat.id} value={flat.flat_number}>
                  {t('auth.register.flatOption', {
                    flat: flat.flat_number,
                    type: flat.flat_type,
                    status: flat.status === 'available'
                      ? t('auth.register.statusAvailable')
                      : t('auth.register.statusOccupied')
                  })}
                </option>
              ))}
            </select>
            {formData.floor_id && formData.floor_id > 0 && flats.length === 0 && (
              <small className="text-muted">{t('auth.register.loadingFlats')}</small>
            )}
          </div>

          <button type="submit" disabled={loading} className="submit-btn">
            {loading ? t('auth.register.submitting') : t('auth.register.submit')}
          </button>
        </form>
        
        <p className="auth-link">
          {t('auth.register.hasAccount')} <Link to="/login">{t('auth.register.loginLink')}</Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
